import React, { useState } from 'react';
import { MButton } from '@/components/ui/atoms/MButtons';
import {
  IconPlus,
  IconX,
  IconFolderPlus,
  IconBellPlus,
  IconFilePlus,
  IconUserPlus,
  IconBubblePlus,
} from '@tabler/icons-react';
import styles from './MFab.module.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { MDocumentUploadOptions } from '@/components/ui/molecules/MDocumentUploadOptions';
import { getMemberInfo } from '@/hooks/useMembers';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

const ctas = [
  { icon: <IconFolderPlus className={styles.ctaIcon} />, text: 'Add document', key: 'document' },
  { icon: <IconBellPlus className={styles.ctaIcon} />, text: 'Add reminder', key: 'reminder' },
  { icon: <IconBubblePlus className={styles.ctaIcon} />, text: 'Add health memory', key: 'health' },
  { icon: <IconFilePlus className={styles.ctaIcon} />, text: 'Add family note', key: 'note' },
  { icon: <IconUserPlus className={styles.ctaIcon} />, text: 'Add family member', key: 'member' },
];

const MFab: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const loggedInMember = getMemberInfo(true);

  const handleCtaClick = (key: string) => {
    setOpen(false);
    switch (key) {
      case 'document':
        // Track document add method click
        trackUserDataLoaded({
          memberInfo: loggedInMember,
          screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
          screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ADD_METHOD,
        });
        setShowDocModal(true);
        break;
      case 'reminder':
        navigate('/reminders?mode=add', { state: { backUrl: location.pathname } });
        break;
      case 'health':
        navigate('/documents/health-memory/add', { state: { backUrl: location.pathname } });
        break;
      case 'member':
        navigate('/profile/family/add-member', { state: { backUrl: location.pathname } });
        break;
      case 'note':
        navigate('/profile/family/notes?mode=add', { state: { backUrl: location.pathname } });
        break;
      default:
        break;
    }
  };

  return (
    <>
      {showDocModal && (
        <MDocumentUploadOptions open={showDocModal} onClose={() => setShowDocModal(false)} />
      )}
      {open && <div className={styles.mfabBackdrop} onClick={() => setOpen(false)} />}
      <div className={`${styles.mfabRoot} ${open ? styles.open : ''}`}>
        <div className={styles.ctaList}>
          {ctas
            .filter(
              (cta) =>
                cta.key !== 'member' || loggedInMember?.family_member_type === 'fo'
            )
            .map((cta) => (
              <div
                className={styles.ctaRow}
                key={cta.key}
                onClick={() => handleCtaClick(cta.key)}
              >
                <div className={styles.ctaText}>{cta.text}</div>
                <button
                  className={styles.ctaCircle}
                  aria-label={cta.text}
                  tabIndex={open ? 0 : -1}
                  type="button"
                >
                  {cta.icon}
                </button>
              </div>
            ))}
        </div>

        <button
          className={styles.fabButton}
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? 'Close' : 'Add'}
          data-fab-button
        >
          <MButton
            variant="icon-only-filled"
            size="md"
            icon={open ? <IconX stroke={2} /> : <IconPlus stroke={2} />}
            state="default"
          />
        </button>
      </div>
    </>
  );
};

export default MFab;